import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { useIntl } from "react-intl";

//Components
import { Icon } from "@mui/material";
import { Button, Chip } from "@gpstrackit/react-controls"

//EndPoints
import { endpoints } from 'Core/defaultValues';

//Views
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import ChipStatus from 'Components/ChipStatus';
import AddIntegration from 'Modules/accounts/.components/addIntegration'
import ViewCerebrumXIntegration from "Modules/accounts/.components/viewCerebrumXIntegration";
import DeleteCerebrumXIntegration from "Modules/accounts/.components/deleteCerebrumXIntegration";

//Helpers
import { getDateFormatted, getCerebrumXParameters, getPMUser, getCerebrumXStatus } from 'Core/data/Helpers';

//Security
import { HasPermission } from "Core/security";

//Notification
import { NotificationManager } from "Components/Notifications";

//Actions
import {
    searchEntityRequest,
    addIntegrationOpenModal,
    viewCerebrumXOpenModal,
    integrationsDeleteCerebrumXOpenModal,
    clearRedux
} from 'Redux/actions';

const TabIntegrations = (props) => {

    const { companyId, tab, height, company } = props;

    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const pmUsers = useSelector(state => state.generalRedux.pmUsers);
    const lang = useSelector(state => state.commonRedux.language);
    const user = useSelector(state => state.securityRedux.user);
    const openModalAddIntegration = useSelector(state => state.addIntegrationRedux.openModal);
    const companyIntegrations = useSelector(state => state.commonRedux["companyIntegrations"]);
    const openModalViewCerebrumXIntegration = useSelector(state => state.viewCerebrumXIntegrationRedux.openModal);
    const openModalDeleteCerebrumXIntegration = useSelector(state => state.deleteCerebrumXIntegrationRedux.openModal);


    const [columns, setColumns] = useState([
        {
            key: 'view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                if (item?.integration?.code === "cerebrumX")
                    return (
                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                            dispatch(viewCerebrumXOpenModal({ companyIntegrationId: item.id }))
                        }}>
                            remove_red_eye
                        </Icon>
                    )
            }
        },
        {
            key: "externalId",
            field: "externalId",
            title: messages['integrations_tab_external_id'],
            width: 120,
            show: true,
            locked: false,
            resize: true,
            code: "integrations_tab_external_id",
            render: (item) => {
                return <span>{item?.externalId}</span>
            },
            export: true,
            functionString: 'return row?.externalId'
        },
        {
            key: "integration_name",
            field: "integration.name",
            title: messages['integrations_tab_integration'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            sort: true,
            code: "integrations_tab_integration",
            render: (item) => {

                return <span>{item?.integration?.name}</span>
            },
            export: true,
            functionString: 'return row?.integration?.name'
        },
        {
            key: "status",
            field: "status",
            title: messages['integrations_tab_status'],
            sort: true,
            width: 200,
            show: true,
            locked: false,
            resize: true,
            code: "integrations_tab_status",
            render: (item) => {
                console.log({status: item?.status})
                if (item?.integration?.code !== "cerebrumX")
                    return (
                        <div style={{ display: 'flex' }}>
                            <ChipStatus label={item?.status} messages={messages} />
                        </div>
                    )
                else {
                    let chip = getCerebrumXStatus(item?.status, messages)
                    return (
                        <div className="chip-status" style={{ width: '100px', textAlign: 'center' }}>
                            <Chip
                                label={chip.name}
                                style={{
                                    backgroundColor: chip.backgroundColor,
                                    color: "#ffffff"
                                }} />
                        </div>
                    )
                }
            },
            export: true,
            functionString: 'return row?.status;'
        },
        {
            key: "parameters",
            field: "parameters",
            title: messages['integrations_tab_parameters'],
            width: 550,
            show: true,
            locked: false,
            resize: true,
            code: "integrations_tab_parameters",
            render: (item) => {
                if (item?.integration?.code === "cerebrumX") {
                    let data = JSON.parse(JSON.stringify(item));
                    if (data?.parameters)
                        data.parameters = JSON.parse(data?.parameters);

                    return <span>{getCerebrumXParameters(data, messages)}</span>
                }else{
                    return <span>{item?.parameters}</span>
                }
            },
            export: true,
            functionString: 'return row?.parameters'
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['notes_tab_created_on'],
            show: true,
            resize: true,
            width: 200,
            sort: true,
            export: true,
            functionString: 'return row?.createdOn && (new Date(`${row?.createdOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "notes_tab_created_on",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime")}</span>
            }
        },
        {
            key: "createdBy",
            field: "createdBy",
            title: messages['notes_tab_created_by'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                let pmUsers = ${JSON.stringify(pmUsers)}
                pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
            `,
            code: "notes_tab_created_by",
            render: (item) => {
                return <span>{item?.createdBy && getPMUser(item?.createdBy, pmUsers)}</span>
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['notes_tab_updated_on'],
            show: true,
            resize: true,
            width: 200,
            sort: true,
            export: true,
            functionString: 'return row?.updatedOn && (new Date(`${row?.updatedOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "notes_tab_updated_on",
            render: (item) => {
                return <span>{item?.updatedOn && getDateFormatted(item?.updatedOn, "date", "datetime")}</span>
            }
        },
        {
            key: "updatedBy",
            field: "updatedBy",
            title: messages['notes_tab_updated_by'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                let pmUsers = ${JSON.stringify(pmUsers)}
                pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.updatedBy)
                return pmUsers.firstName + " " + pmUsers.lastName || row?.updatedBy
            `,
            code: "notes_tab_updated_by",
            render: (item) => {
                return <span>{item?.updatedBy && getPMUser(item?.updatedBy, pmUsers)}</span>
            }
        }

    ])

    const [searchFields, setSearchFields] = useState(["integration_name", "status"]);

    useEffect(() => {
        (!pmUsers || pmUsers.length === 0) && dispatch(searchEntityRequest({ key: "pmUsers" }));
    }, []);

    useEffect(() => {
        if (pmUsers.length > 0) {
            // Use functional updater to preserve preference-restored 'show' flags
            setColumns(prev => prev.map((item) => ({
                ...item,
                title: messages[item.code],
                functionString: item.key === "createdBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
                ` : item.key === "updatedBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.updatedBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.updatedBy
                ` : item.functionString,
                render: item.key === "createdBy" ? (item) => {
                    let label = item?.createdBy ? getPMUser(item?.createdBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.key === "updatedBy" ? (item) => {
                    let label = item?.updatedBy ? getPMUser(item?.updatedBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.key === "status" ? (item) => {
                    if (item?.integration?.code !== "cerebrumX")
                        return (
                            <div style={{ display: 'flex' }}>
                                <ChipStatus label={item?.status} messages={messages} />
                            </div>
                        )
                    else {
                        let chip = getCerebrumXStatus(item?.status, messages)
                        return (
                            <div className="chip-status" style={{ width: '100px', textAlign: 'center' }}>
                                <Chip
                                    label={chip.name}
                                    style={{
                                        backgroundColor: chip.backgroundColor,
                                        color: "#ffffff"
                                    }} />
                            </div>
                        )
                    }
                } : item.render,
            })))
        }
    }, [pmUsers, lang])

    useEffect(() => {
        return () => {
            dispatch(clearRedux("COMMON", { 'companyIntegrations': {} }))
        }
    }, [])

    // Memoized config objects for DataViewInfiniteScroll
    const graphqlConfig = useMemo(() => ({
        function: "searchCompanyIntegrations",
        sortType: "CompanyIntegrationSortInput",
        conditionsType: "CompanyIntegrationSearchInput",
        customQuery: "id integrationId integration { id code }",
        dynamicQuery: true,
    }), []);

    const searchConfig = useMemo(() => ({
        conditions: ["integration_name", "status"],
        optionValue: "id",
        optionLabel: "id",
        extraField: "integration { id name } status",
        custom: { fields: ["companyId"], terms: [company?.id], exact: true, conditionOperator: "AND" },
        openCustomOptions: (item) => {
            // Typed tags have no integration data - return label as-is
            if (!item.integration) {
                return item?.label || item.id;
            }
            // Selected from dropdown - apply custom formatting
            const integrationName = item.integration?.name || '';
            return `${integrationName || item.id} ${item.status ? `(${item.status})` : ""}`;
        }
    }), []);

    const exportConfig = useMemo(() => ({
        fileName: "companyIntegrations",
    }), []);

    const uiConfig = useMemo(() => ({
        hideFilters: true,
        hideSplit: true,
        topHeight: 200,
        hideAllInPagination: true,
        height: height && height + "px" || "500px",
    }), [height]);

    const defaultsConfig = useMemo(() => ({
        sort: { field: "integration_name", order: "ASC" },
        rowsPerPage: 25,
        valuesPerPage: [10, 25, 50]
    }), []);

    // Extract handlers above JSX for readability
    const handleChangeConditions = useCallback((tags, conditions, total, newColumns, rows, sort) => {
        //current conditions
        let currentConditions = {}
        let currentTags = {}

        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
        }

        // Filter out conditions that are rebuilt from tags below.
        // 'id' is the legacy field name (pre-rename), 'integrationId' is the current field name.
        // Both must be excluded so stale preferences with old field names don't create duplicates.
        currentConditions = currentConditions?.filter((item) =>
            item?.fields[0] !== 'integration_name' &&
            item?.fields[0] !== 'status' &&
            item?.fields[0] !== 'companyId' &&
            item?.fields[0] !== 'id' &&
            item?.fields[0] !== 'integrationId'
        );

        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length > 0) {
            //New Items
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }

        let tagsByLabel = currentTags.filter((item) => typeof item.id === "string");

        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.id))
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        currentConditions.push({ fields: ["companyId"], terms: [companyId], exact: true, conditionOperator: "AND" });

        // FIX: Always forward newSort so preference-restored sort isn't lost in tags-only branch
        if (!conditions && tags) {
            tableRef?.current?.getData({ newConditions: currentConditions, newSort: sort });
        } else {
            tableRef?.current?.getData({ newRows: rows, newSort: sort, newConditions: currentConditions, newColumns });
        }
    }, [companyId, searchFields]);

    const handleChangeColumns = useCallback((items) => {
        let currentColumns = items.map((item) => ({
            ...item,
            title: item.code ? messages[item.code] : item.title,
            /* render: item.key === "status" ? (item) => {
                return (
                    <div style={{ display: 'flex' }}>
                        <ChipStatus statusCode={item?.status} messages={messages} />
                    </div>
                )
            } : item.render, */
        }))
        setColumns(currentColumns)
    }, [messages]);

    const extraButton = useCallback(() => {
        return (
            <div style={{ margin: '0 5px' }}>
                <Button
                    disabled={!HasPermission(user, "intgrtns", "c")}
                    className="btn-dark-blue"
                    onClick={(e) => {
                        e.preventDefault();
                        dispatch(addIntegrationOpenModal())
                    }}
                >
                    {messages['integrations_tab_add_integration']}
                </Button>
            </div>
        )
    }, [user, messages, dispatch]);

    const menuOptions = useMemo(() => [
        {
            id: "delete-integration",
            label: messages['delete'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "intgrtns", "d"),
            action: (items) => {
                if (items[0].integration.code === "cerebrumX" && HasPermission(user, "crbint", "d"))
                    dispatch(integrationsDeleteCerebrumXOpenModal({ fleetId: items[0].externalId, companyIntegrationId: items[0].id }))
                else if (items[0].integration.code === "cerebrumX" && !HasPermission(user, "crbint", "d")) {
                    NotificationManager.error(
                        messages['integrations_tab_cerebrumx_permission_error'],
                        messages['integrations_tab_title'],
                        3000
                    );
                } else {
                    dispatch(integrationsDeleteCerebrumXOpenModal({ companyIntegrationId: items[0].id }))
                }
            }
        }
    ], [messages, user, dispatch]);

    return (
        <div className="integrations-tab" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                name="pm-companyIntegrations"
                entity="companyIntegrations"
                columns={columns}
                infiniteScroll={false}
                selectionConfig={{ showCounter: false }}
                graphqlConfig={graphqlConfig}
                searchConfig={searchConfig}
                exportConfig={exportConfig}
                uiConfig={uiConfig}
                defaultsConfig={defaultsConfig}
                onChangeConditions={handleChangeConditions}
                onChangeColumns={handleChangeColumns}
                tableButtons={["export", 'orderColumn']}
                extraButton={extraButton}
                options={menuOptions}
            />
            {openModalAddIntegration && <AddIntegration companyIntegrations={companyIntegrations?.items || []} company={company} tableRef={tableRef} />}
            {openModalViewCerebrumXIntegration && <ViewCerebrumXIntegration tableRef={tableRef} company={company} />}
            {openModalDeleteCerebrumXIntegration && <DeleteCerebrumXIntegration tableRef={tableRef} />}
        </div>
    )
}
export default TabIntegrations;